import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import ProgramBuilder from './ProgramBuilder';
import ProgramQuickEdit from './ProgramQuickEdit';
import ProgramsList from './ProgramsList';
import ExerciseLibraryManager from './ExerciseLibraryManager';
import ClientManagement from './ClientManagement';
import AdminProgramViewer from './AdminProgramViewer';
import GifAudit from './GifAudit';
import AuditLogViewer from './components/AuditLogViewer';
import MyCoaches from './components/MyCoaches';
import CoachDashboard from './components/CoachDashboard';
import DeletionRequests from './components/DeletionRequests';
import CipherNutritionPanel from './components/CipherNutritionPanel';
import PeoplePanel from './components/PeoplePanel';
import { useUserRole } from './hooks/useUserRole';
import { useNeedsYou } from './hooks/useNeedsYou';
import { generateProgramPDF } from './generateProgramPDF';
import { toastError, toastSuccess } from './ui/toast';
import { getPositionLabel } from './constants/blockTypes';
import {
  ClipboardList, BookOpen, Dumbbell, Users, Film,
  Award, FileText, LayoutDashboard, LogOut, Trash2, UsersRound, Activity
} from 'lucide-react';
import cipherLogo from './assets/cipher-icon-only-transparent.png';
import { colors as theme } from './theme';


const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const AdminPanel = ({ onLogout, onBackToDashboard }) => {
  const [currentView, setCurrentView] = useState('main');
  const [initialClientId, setInitialClientId] = useState(null);
  const [initialClientTab, setInitialClientTab] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [, setUnresolvedFlagsCount] = useState(0);
  const [deletionRequestsCount, setDeletionRequestsCount] = useState(0);
  const [userName, setUserName] = useState('');
  const [editProgramId, setEditProgramId] = useState(null);
  const [, setEditProgramActiveCount] = useState(0);
  const [editReturnView, setEditReturnView] = useState(null);
  const { isAdmin, isSuperAdmin } = useUserRole();
  // Who needs the coach (plan 004): loaded once, shown as a line here and as a group in Manage Clients
  const needs = useNeedsYou();

  // Show persistent scrollbar on desktop while admin panel is mounted
  useEffect(() => {
    document.documentElement.classList.add('admin-panel-active');
    return () => document.documentElement.classList.remove('admin-panel-active');
  }, []);

  useEffect(() => {
    const fetchName = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase.from('users').select('name').eq('auth_user_id', user.id).single();
      if (data?.name) setUserName(data.name.split(' ')[0]);
    };
    fetchName();
  }, []);

  // Load pending users count when component mounts
  useEffect(() => {
    loadPendingUsersCount();
  }, []);

  // Re-fetch badge counts whenever returning to main menu (e.g. after resolving flags)
  useEffect(() => {
    if (currentView === 'main') {
      loadPendingUsersCount();
    }
  }, [currentView]);

  const loadPendingUsersCount = async () => {
    try {
      const { count: flagCount } = await supabase
        .from('exercise_flags')
        .select('*', { count: 'exact', head: true })
        .is('resolved_at', null);
      setUnresolvedFlagsCount(flagCount || 0);
    } catch (err) {
      console.error('Error loading unresolved flags count:', err);
    }

    try {
      const { count: delCount } = await supabase
        .from('deletion_requests')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');
      setDeletionRequestsCount(delCount || 0);
    } catch (err) {
      console.error('Error loading deletion requests count:', err);
    }
  };

  const handleGeneratePDF = async (programId) => {
    try {
      toastSuccess('Starting PDF generation...');
      // Get program details
      const { data: program, error: programError } = await supabase
        .from('programs')
        .select('*')
        .eq('id', programId)
        .single();

      if (programError) throw programError;

      // Get weeks
      const { data: weeks, error: weeksError } = await supabase
        .from('program_weeks')
        .select('*')
        .eq('program_id', programId)
        .order('week_number');

      if (weeksError) throw weeksError;

      // Get days for all weeks
      const weekIds = weeks.map(w => w.id);
      const { data: days, error: daysError } = await supabase
        .from('program_days')
        .select('*')
        .in('week_id', weekIds)
        .order('day_number');

      if (daysError) throw daysError;

      const usesBlocks = program.uses_blocks;
      const dayIds = days.map(d => d.id);

      // Fetch blocks/items (or legacy exercises) for every day in a handful of
      // batched queries instead of firing one set of queries per day — with
      // many days in parallel, identical-shaped concurrent queries can collide
      // on Supabase's pooled connections and come back as 500s.
      let daysWithExercises;

      if (usesBlocks) {
        const { data: blocksData } = dayIds.length
          ? await supabase.from('exercise_blocks').select('*').in('day_id', dayIds).order('block_order', { ascending: true })
          : { data: [] };

        const blockIds = (blocksData || []).map(b => b.id);
        const { data: blockItemsData } = blockIds.length
          ? await supabase.from('block_items').select('*').in('block_id', blockIds).order('position', { ascending: true })
          : { data: [] };

        const exerciseIds = [...new Set((blockItemsData || []).map(bi => bi.exercise_id))];
        let exercisesMap = {};
        if (exerciseIds.length > 0) {
          const { data: exercisesData } = await supabase.from('exercises').select('*').in('id', exerciseIds);
          (exercisesData || []).forEach(ex => { exercisesMap[ex.id] = ex; });
        }

        const blocksByDay = {};
        (blocksData || []).forEach(b => {
          if (!blocksByDay[b.day_id]) blocksByDay[b.day_id] = [];
          blocksByDay[b.day_id].push(b);
        });

        daysWithExercises = days.map(day => {
          // Attach items to blocks — blockIndex is per-day, matching the original per-day fetch
          const blocksWithItems = (blocksByDay[day.id] || []).map((block, blockIndex) => ({
            ...block,
            items: (blockItemsData || [])
              .filter(bi => bi.block_id === block.id)
              .map(bi => ({
                ...bi,
                exercise: exercisesMap[bi.exercise_id] || null,
                position_label: getPositionLabel(blockIndex, bi.position),
              })),
          }));

          return {
            ...day,
            blocks: blocksWithItems,
            exercises: [],
          };
        });
      } else {
        // Legacy: fetch program_exercises
        const { data: programExercisesData } = dayIds.length
          ? await supabase.from('program_exercises').select('*').in('day_id', dayIds).order('exercise_order', { ascending: true })
          : { data: [] };

        const exerciseIds = [...new Set((programExercisesData || []).map(pe => pe.exercise_id))];
        let exercisesMap = {};
        if (exerciseIds.length > 0) {
          const { data: exercisesData } = await supabase.from('exercises').select('*').in('id', exerciseIds);
          (exercisesData || []).forEach(ex => { exercisesMap[ex.id] = ex; });
        }

        const exercisesByDay = {};
        (programExercisesData || []).forEach(pe => {
          if (!exercisesByDay[pe.day_id]) exercisesByDay[pe.day_id] = [];
          exercisesByDay[pe.day_id].push({
            exercise: exercisesMap[pe.exercise_id] || { name: 'Unknown Exercise' },
            sets: pe.sets,
            reps: pe.reps,
            load_percentage: pe.load_percentage,
            notes: pe.notes,
          });
        });

        daysWithExercises = days.map(day => ({
          ...day,
          exercises: exercisesByDay[day.id] || [],
          blocks: [],
        }));
      }

      // Get client info if assigned
      let clientName = 'Unassigned Program';
      if (program.user_id) {
        // Try users table first
        const { data: user } = await supabase
          .from('users')
          .select('name')
          .eq('auth_user_id', program.user_id)
          .single();

        if (user) {
          clientName = user.name;
        } else {
          // Fallback to onboarding_data
          const { data: client } = await supabase
            .from('onboarding_data')
            .select('full_name')
            .eq('user_id', program.user_id)
            .single();

          if (client) clientName = client.full_name;
        }
      }

      // Structure the data
      const programData = {
        name: program.name,
        description: program.description,
        clientName: clientName,
        weeks: weeks.map(week => ({
          ...week,
          days: daysWithExercises.filter(day => day.week_id === week.id)
        }))
      };

      // Call PDF generator
      const result = await generateProgramPDF(programData, programData.weeks, { createdAt: new Date() });
      toastSuccess(`PDF generated: ${result}`);

    } catch (err) {
      console.error('AdminPanel: Error generating PDF:', err);
      console.error('Error stack:', err.stack);
      console.error('Error details:', {
        message: err.message,
        name: err.name,
        cause: err.cause
      });
      toastError(`Error generating PDF: ${err.message || 'Unknown error'}`);
    }
  };

  // Show different views
  if (selectedProgram) {
    return (
      <AdminProgramViewer
        program={selectedProgram}
        onBack={() => setSelectedProgram(null)}
      />
    );
  }

  if (currentView === 'programs') {
    return (
      <ProgramsList
        onSelectProgram={(program) => setSelectedProgram(program)}
        onGeneratePDF={handleGeneratePDF}
        onBack={() => setCurrentView('main')}
        onEditProgram={(programId, activeCount) => {
          setEditProgramId(programId);
          setEditProgramActiveCount(activeCount);
          setCurrentView('edit-program');
        }}
      />
    );
  }

  if (currentView === 'builder') {
    return <ProgramBuilder onBack={() => setCurrentView('main')} />;
  }

  if (currentView === 'edit-program') {
    return (
      <ProgramQuickEdit
        programId={editProgramId}
        onBack={() => {
          setCurrentView(editReturnView || 'programs');
          setEditProgramId(null);
          setEditProgramActiveCount(0);
          setEditReturnView(null);
        }}
      />
    );
  }

  if (currentView === 'exercises') {
    return <ExerciseLibraryManager onBack={() => setCurrentView('main')} />;
  }

  if (currentView === 'clients') {
    return (
      <ClientManagement
        onBack={() => { setInitialClientId(null); setInitialClientTab(null); setCurrentView('main'); }}
        initialClientId={initialClientId}
        initialTab={initialClientTab}
        needs={needs}
        onEditProgram={(programId, activeCount) => {
          setEditProgramId(programId);
          setEditProgramActiveCount(activeCount);
          setEditReturnView('clients');
          setCurrentView('edit-program');
        }}
      />
    );
  }

  if (currentView === 'gifaudit') {
    return <GifAudit onBack={() => setCurrentView('main')} />;
  }

  if (currentView === 'audit-logs') {
    return <AuditLogViewer onBack={() => setCurrentView('main')} />;
  }

  if (currentView === 'deletion-requests') {
    return (
      <DeletionRequests
        onBack={() => {
          setCurrentView('main');
          loadPendingUsersCount();
        }}
        onCountChange={setDeletionRequestsCount}
      />
    );
  }

  if (currentView === 'my-coaches') {
    return <MyCoaches onBack={() => setCurrentView('main')} />;
  }

if (currentView === 'coach-dashboard') {
    return (
      <div style={{ padding: '24px 20px', backgroundColor: theme.bg, minHeight: '100vh', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
          <button
            onClick={() => setCurrentView('main')}
            style={{ background: 'none', border: 'none', color: theme.textMuted, cursor: 'pointer', fontSize: '14px', padding: 0 }}
          >
            ← Back
          </button>
          <h2 style={{ color: theme.text, fontSize: '20px', fontWeight: 700, margin: 0 }}>Client Activity</h2>
        </div>
        <CoachDashboard onViewClient={(client) => { setInitialClientId(client.id); setCurrentView('clients'); }} />
      </div>
    );
  }

  if (currentView === 'cipher-nutrition') {
    return <CipherNutritionPanel onBack={() => setCurrentView('main')} />;
  }

  if (currentView === 'people') {
    return (
      <PeoplePanel
        onBack={() => setCurrentView('main')}
        onViewClient={(fitnessId) => {
          setInitialClientId(fitnessId);
          setCurrentView('clients');
        }}
      />
    );
  }

  // Daily coaching first, then the tools used now and then (15 October 2026, Codex's premium
  // review, 10). Every screen is still here.
  const menuItems = [
    { view: 'clients',         label: 'Manage Clients',     desc: 'Programs, messages, check-ins', Icon: Users },
    { view: 'coach-dashboard', label: 'Client Activity',    desc: 'Who trained, and flags',       Icon: Activity },
    { view: 'builder',         label: 'Build Program',      desc: 'Create training programs',    Icon: ClipboardList },
    { view: 'programs',        label: 'Programs Library',   desc: 'View & manage programs',      Icon: BookOpen },
    ...(isAdmin || isSuperAdmin ? [{ view: 'people', label: 'People', desc: 'All members in one place', Icon: UsersRound }] : []),
  ];
  const toolItems = [
    { view: 'exercises',       label: 'Exercise Library',   desc: 'Manage exercises',             Icon: Dumbbell },
    { view: 'gifaudit',        label: 'GIF Audit',          desc: 'Verify exercise GIFs',         Icon: Film },
    ...(isAdmin || isSuperAdmin ? [{ view: 'my-coaches', label: 'My Coaches', desc: 'View coaches & clients', Icon: Award }] : []),
    ...(isAdmin || isSuperAdmin ? [{ view: 'audit-logs', label: 'Audit Logs', desc: 'View system activity', Icon: FileText }] : []),
    { view: 'deletion-requests', label: 'Deletion Requests', desc: 'Review account deletion asks',  Icon: Trash2, badge: deletionRequestsCount },
  ];

  // Main menu
  return (
    <div style={{ padding: '24px 20px', backgroundColor: theme.bg, minHeight: '100vh', boxSizing: 'border-box' }}>

      {/* Top bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '36px' }}>
        {/* Brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '68px', height: '68px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(212,175,55,0.1) 0%, rgba(212,175,55,0) 70%)',
            flexShrink: 0,
          }}>
            <img src={cipherLogo} alt="Cipher" style={{
              width: '64px', height: '64px',
            }} />
          </div>
          <div>
            <div style={{ color: theme.gold, fontSize: '18px', fontWeight: 700, letterSpacing: '2px', fontFamily: "'Orbitron', sans-serif" }}>
              CIPHER
            </div>
            <div style={{ color: theme.textMuted, fontSize: '12px', marginTop: '1px' }}>
              Coach Dashboard
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {onBackToDashboard && (
            <button onClick={onBackToDashboard} style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              backgroundColor: 'transparent', border: `1px solid ${theme.gold}`,
              color: theme.gold, padding: '10px 18px', borderRadius: '8px',
              cursor: 'pointer', fontSize: '13px', fontWeight: 600,
            }}>
              <LayoutDashboard size={15} /> Dashboard
            </button>
          )}
<button onClick={onLogout} style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            backgroundColor: 'transparent', border: `1px solid ${theme.danger}`,
            color: theme.danger, padding: '10px 18px', borderRadius: '8px',
            cursor: 'pointer', fontSize: '13px', fontWeight: 600,
          }}>
            <LogOut size={15} /> Logout
          </button>
        </div>
      </div>

      {/* Greeting */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ color: theme.text, fontSize: '28px', fontWeight: 700, margin: 0 }}>
          {getGreeting()}{userName ? `, ${userName}` : ''}.
        </h1>
        <p style={{ color: theme.textMuted, fontSize: '14px', marginTop: '6px' }}>
          Here's your Cipher control panel. What are we working on today?
        </p>
      </div>

      {/* Who needs the coach: one line here; the clients themselves are at the top of Manage Clients
          (plan 004, 15 October 2026: twelve cards on this screen were too many) */}
      <div style={{ maxWidth: '900px', marginBottom: '28px' }}>
        <button
          type="button"
          onClick={() => { setInitialClientId(null); setInitialClientTab(null); setCurrentView('clients'); }}
          style={{
            width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px',
            padding: '14px 16px', borderRadius: '14px', cursor: 'pointer', fontFamily: 'inherit',
            backgroundColor: theme.surface1,
            border: `1px solid ${needs.rows.length ? 'rgba(212,175,55,0.45)' : theme.surface3}`,
            color: needs.failed ? theme.danger : needs.rows.length ? theme.gold : theme.textMuted,
            fontSize: '15px', fontWeight: 600,
          }}
        >
          <span style={{ flex: 1 }}>
            {needs.loading && !needs.rows.length
              ? 'Checking who needs you…'
              : needs.failed
                ? 'Could not check who needs you. Open Manage Clients to try again'
                : needs.rows.length
                  ? `${needs.rows.length} ${needs.rows.length === 1 ? 'client needs' : 'clients need'} you`
                  : '✓ Nobody needs you right now'}
          </span>
          <span aria-hidden="true">→</span>
        </button>
      </div>

      {/* Divider */}
      <div style={{ height: '1px', backgroundColor: theme.surface3, marginBottom: '28px' }} />

      {/* Menu: daily work, then tools. On a phone each is a compact row (index.css, .admin-menu). */}
      {[{ heading: null, items: menuItems }, { heading: 'Tools', items: toolItems }].map(({ heading, items }) => (
        <div key={heading || 'main'} style={{ maxWidth: '900px', marginBottom: '24px' }}>
          {heading && (
            <div style={{ color: theme.textMuted, fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '10px' }}>
              {heading}
            </div>
          )}
          <div className="admin-menu" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: '16px',
            width: '100%',
            boxSizing: 'border-box',
          }}>
            {items.map(({ view, label, desc, Icon, badge }) => (
              <div
                key={view}
                role="button"
                tabIndex={0}
                className="admin-menu-item"
                onClick={() => setCurrentView(view)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCurrentView(view); } }}
                style={{
                  backgroundColor: theme.surface1,
                  border: `1px solid ${theme.surface3}`,
                  padding: '24px 20px',
                  borderRadius: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  position: 'relative',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = theme.gold;
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(212,175,55,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = theme.surface3;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {badge > 0 && (
                  <div className="admin-menu-badge" style={{
                    position: 'absolute', top: '12px', right: '12px',
                    backgroundColor: theme.danger, color: theme.text,
                    borderRadius: '50%', width: '22px', height: '22px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '11px', fontWeight: 700,
                  }}>
                    {badge}
                  </div>
                )}
                <div className="admin-menu-icon" style={{
                  width: '44px', height: '44px', borderRadius: '8px',
                  backgroundColor: 'rgba(212,175,55,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '14px', flexShrink: 0,
                }}>
                  <Icon size={22} color={theme.gold} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: theme.text, fontSize: '15px', fontWeight: 600, marginBottom: '5px' }}>
                    {label}
                  </div>
                  <div style={{ color: theme.textMuted, fontSize: '12px' }}>
                    {desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

    </div>
  );
};


export default AdminPanel;
